import { createHook, createComponent } from 'reakit-system';

import {
  background,
  BackgroundProps,
  border,
  BorderProps,
  color,
  ColorProps,
  compose,
  flexbox,
  FlexboxProps,
  grid,
  GridProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  shadow,
  ShadowProps,
  space,
  SpaceProps,
} from 'styled-system';
import { cx, css } from '@emotion/css';
import { Theme } from '@mos-cat/ds-theme';
import { useTheme } from '../hooks/useTheme';
import { StylesOptions, useStyles } from '../hooks/useStyles';

import { useShorthandAndFullPropConflictWarning } from '../hooks/useShorthandAndFullPropConflictWarning';

let boxScales = compose(
  border,
  position,
  space,
  border,
  layout,
  color,
  flexbox,
  grid,
  background,
  shadow
);

export type BoxOptions = BackgroundProps<Theme> &
  BorderProps<Theme> &
  ColorProps<Theme> &
  FlexboxProps<Theme> &
  GridProps<Theme> &
  LayoutProps<Theme> &
  PositionProps<Theme> &
  ShadowProps<Theme> &
  SpaceProps<Theme> &
  StylesOptions;

export type BoxHTMLProps = Omit<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- здесь нужно any
  React.HTMLAttributes<any> & React.RefAttributes<any>,
  keyof BoxOptions
>;

export type BoxProps = BoxOptions & BoxHTMLProps;

const useWarnings = (options: BoxOptions) => {
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.mt || !options.marginTop),
    'Box',
    ['mt', 'marginTop']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.mb || !options.marginBottom),
    'Box',
    ['mb', 'marginBottom']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.mr || !options.marginRight),
    'Box',
    ['mr', 'marginRight']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.ml || !options.marginLeft),
    'Box',
    ['ml', 'marginLeft']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.m || !options.margin),
    'Box',
    ['m', 'margin']
  );

  useShorthandAndFullPropConflictWarning(
    Boolean(!options.pt || !options.paddingTop),
    'Box',
    ['pt', 'paddingTop']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.pb || !options.paddingBottom),
    'Box',
    ['pb', 'paddingBottom']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.pr || !options.paddingRight),
    'Box',
    ['pr', 'paddingRight']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.pl || !options.paddingLeft),
    'Box',
    ['pl', 'paddingLeft']
  );
  useShorthandAndFullPropConflictWarning(
    Boolean(!options.p || !options.padding),
    'Box',
    ['p', 'padding']
  );
};

const useBox = createHook<BoxOptions, BoxHTMLProps>({
  name: 'Box',
  keys: boxScales.propNames,
  compose: [useTheme, useStyles],
  useProps(options, props) {
    useWarnings(options);
    return {
      ...props,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- здесь на наши типы
      className: cx(props.className, css({ label: 'Box' }, boxScales(options))),
    };
  },
});

const Box = createComponent({
  useHook: useBox,
  as: 'div',
  memo: true,
});

export { Box, useBox };
